import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { getClinicPortalServiceTypeHeader } from '@/hooks/main/clinicPortalServiceType';

/** 日別 — every visit bills the 明細書発行加算. */
export const MEISAI_ISSUE_TYPE_DAILY = 1;
/** 月別 — only the earliest visit of the month bills it. System default. */
export const MEISAI_ISSUE_TYPE_MONTHLY = 2;

export type MeisaiIssueType = typeof MEISAI_ISSUE_TYPE_DAILY | typeof MEISAI_ISSUE_TYPE_MONTHLY;

export interface MeisaiIssueSettingPatient {
  patient_id: number;
  serial_number: number | string | null;
  name: string | null;
  name_kana: string | null;
  /** Setting in force this month. Falls back to the clinic default when unset. */
  current_issue_type: MeisaiIssueType;
  /** Booked for next month; null when nothing is booked yet. */
  next_issue_type: MeisaiIssueType | null;
}

export interface MeisaiIssueSettingListResult {
  current_year_month: string;
  next_year_month: string;
  default_issue_type: MeisaiIssueType;
  patients: MeisaiIssueSettingPatient[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

/** BD No 5 — the three sortable columns. */
export type MeisaiIssueSortField = 'serial_number' | 'name' | 'name_kana';

export interface MeisaiIssueSettingListParams {
  keyword?: string;
  treatment_from?: string;
  treatment_to?: string;
  per_page?: number;
  page?: number;
  /** Sorting is server-side so it orders every row, then paginates. */
  sort?: MeisaiIssueSortField;
  direction?: 'asc' | 'desc';
}

interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

export async function fetchMeisaiIssueSettings(
  siteType: string,
  clinicNumber: string,
  params: MeisaiIssueSettingListParams
): Promise<MeisaiIssueSettingListResult> {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/clinic-informations/meisai-issue-settings'),
    { headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() }, params }
  );
  return (response.data as Envelope<MeisaiIssueSettingListResult>).data;
}

export async function updateMeisaiIssueSetting(
  siteType: string,
  clinicNumber: string,
  payload: { patient_id: number; apply_year_month: string; issue_type: MeisaiIssueType }
): Promise<void> {
  await apiClient.put(
    clinicApiPath(siteType, clinicNumber, '/clinic-informations/meisai-issue-settings'),
    payload,
    { headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() } }
  );
}

export async function updateMeisaiIssueDefault(
  siteType: string,
  clinicNumber: string,
  issueType: MeisaiIssueType
): Promise<void> {
  await apiClient.put(
    clinicApiPath(siteType, clinicNumber, '/clinic-informations/meisai-issue-settings/default'),
    { issue_type: issueType },
    { headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() } }
  );
}

/**
 * 令和8年法改正 No 5 — 明細書発行加算 state of a patient's 施術月, for the visit form.
 *
 * `is_earliest_of_month` cannot be derived from the visit's own flag: the user is allowed
 * to untick the earliest visit, after which every visit of the month reads false and the
 * checkbox would disappear with no way to tick it again.
 */
export interface MeisaiIssueVisitStatus {
  issue_type: MeisaiIssueType;
  is_earliest_of_month: boolean;
  /** Billing days in the month excluding the visit being edited. */
  other_on_days: number;
  /** Billing days in the month counting every visit. */
  month_on_days: number;
  /**
   * Every 来院 visit of the month across all 施術師. The visit form overlays its own unsaved
   * adds/moves/deletes on this to work out which visit is earliest right now — the flags
   * above only describe saved data.
   *
   * Rows 訪問入力 created are absent: they carry no 明細書発行加算 and do not compete for the
   * month's earliest visit.
   */
  month_visits: Array<{ id: number; visit_date: string }>;
}

export async function fetchMeisaiIssueVisitStatus(
  siteType: string,
  clinicNumber: string,
  params: { patient_id: number; visit_date: string; visit_id?: number }
): Promise<MeisaiIssueVisitStatus> {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, '/visits/meisai-issue-status'),
    { headers: { 'X-Service-Type': getClinicPortalServiceTypeHeader() }, params }
  );
  return (response.data as Envelope<MeisaiIssueVisitStatus>).data;
}
